import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, X, Loader, Check, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { mediaAttachmentService } from '../../../services/mediaAttachmentService';
import { MediaAttachmentDto } from '../../../types/mediaAttachment';
import { useTranslation } from '../../../contexts/I18nProvider';
import { useOperatorDisplayNames } from '../../../hooks/useOperatorDisplayNames';
import { useMediaContentUrls } from '../../../hooks/useMediaContentUrls';

interface MediaCaptureButtonProps {
  entityType: string;
  entityKey: string;
  onCaptured?: () => void;
  className?: string;
}

/**
 * Compress an image file to JPEG with max dimensions and quality.
 * Returns a base64 data URL string.
 * Eksportert for gjenbruk (Utlevering 36334240: samme bilde lastes opp på flere plukklister).
 */
export async function compressImage(file: File, maxWidth = 1920, quality = 0.8): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export const MediaCaptureButton: React.FC<MediaCaptureButtonProps> = ({
  entityType,
  entityKey,
  onCaptured,
  className = ''
}) => {
  const { t } = useTranslation();
  const { getDisplayName } = useOperatorDisplayNames();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<MediaAttachmentDto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bildekilde uavhengig av lagring (inline dataUrl eller disk via media/{id}/content) — TW 36393708
  const contentUrls = useMediaContentUrls(attachments);

  const loadAttachments = useCallback(async () => {
    try {
      const result = await mediaAttachmentService.getAttachments(entityType, entityKey);
      setAttachments(result.filter(a => a.mediaType === 'photo'));
    } catch (err) {
      console.error('Failed to load attachments:', err);
    }
  }, [entityType, entityKey]);

  useEffect(() => {
    if (entityKey) {
      loadAttachments();
    }
  }, [entityKey, loadAttachments]);

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file);
      setPreviewUrl(compressed);
      setPreviewFile(file);
      setDescription('');
      setError(null);
    } catch (err) {
      console.error('Failed to compress image:', err);
      setError(t('media.uploadError', {}, 'Kunne ikke behandle bildet'));
    }

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!previewUrl) return;

    try {
      setUploading(true);
      setError(null);

      await mediaAttachmentService.addAttachment({
        entityType,
        entityKey,
        mediaType: 'photo',
        contentType: 'image/jpeg',
        fileName: previewFile?.name || `photo_${Date.now()}.jpg`,
        dataUrl: previewUrl,
        description: description.trim() || undefined,
        tags: 'photo'
      });

      setPreviewUrl(null);
      setPreviewFile(null);
      setDescription('');
      await loadAttachments();
      onCaptured?.();
    } catch (err) {
      console.error('Failed to upload:', err);
      setError(t('media.uploadError', {}, 'Kunne ikke laste opp bildet'));
    } finally {
      setUploading(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewUrl(null);
    setPreviewFile(null);
    setDescription('');
    setError(null);
  };

  const handleDeleteAttachment = async (id: number) => {
    try {
      await mediaAttachmentService.deleteAttachment(id);
      setLightboxIndex(null);
      await loadAttachments();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const photoCount = attachments.length;

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Camera button with badge — compact icon on mobile, full button on desktop */}
      <button
        onClick={handleCameraClick}
        className="relative flex items-center justify-center min-w-[32px] w-8 h-8 sm:min-w-0 sm:w-auto sm:h-auto sm:gap-1.5 sm:px-3 sm:py-2 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors sm:bg-blue-50 sm:border sm:border-blue-200"
        title={t('media.capturePhoto', {}, 'Ta bilde')}
      >
        <Camera size={18} className="sm:hidden" />
        <Camera size={18} className="hidden sm:block" />
        <span className="hidden sm:inline text-sm font-medium">{t('media.capturePhoto', {}, 'Ta bilde')}</span>
        {photoCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {photoCount}
          </span>
        )}
      </button>

      {/* Thumbnail strip — hidden on mobile, max 3 on desktop */}
      {attachments.length > 0 && (
        <div className="hidden sm:flex gap-1">
          {attachments.slice(0, 3).map((att, idx) => (
            <button
              key={att.id}
              onClick={() => setLightboxIndex(idx)}
              className="w-8 h-8 rounded border border-gray-300 overflow-hidden hover:border-blue-400 transition-colors flex-shrink-0"
            >
              {contentUrls[att.id] ? (
                <img src={contentUrls[att.id]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <Camera className="w-4 h-4 text-gray-400" />
                </div>
              )}
            </button>
          ))}
          {attachments.length > 3 && (
            <div className="w-8 h-8 rounded border border-gray-200 bg-gray-50 flex items-center justify-center text-xs text-gray-500 font-medium flex-shrink-0">
              +{attachments.length - 3}
            </div>
          )}
        </div>
      )}

      {/* Preview modal - shown after capturing a photo */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{t('media.capturePhoto', {}, 'Ta bilde')}</h3>
              <button onClick={handleCancelPreview} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Image preview */}
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <img src={previewUrl} alt="Preview" className="w-full max-h-64 object-contain bg-gray-50" />
              </div>

              {/* Optional description */}
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('media.addDescription', {}, 'Legg til beskrivelse (valgfritt)')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={uploading}
              />

              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
            </div>

            <div className="flex gap-3 p-4 border-t">
              <button
                onClick={handleCancelPreview}
                disabled={uploading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    {t('media.uploading', {}, 'Laster opp...')}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {t('common.buttons.save', {}, 'Lagre')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox modal - shown when clicking a thumbnail */}
      {lightboxIndex !== null && attachments[lightboxIndex] && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-2xl">
            {/* Close button */}
            <button
              onClick={() => setLightboxIndex(null)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Image */}
            <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
              <div className="bg-gray-50">
                {contentUrls[attachments[lightboxIndex].id] ? (
                  <img
                    src={contentUrls[attachments[lightboxIndex].id]}
                    alt={attachments[lightboxIndex].description || ''}
                    className="w-full max-h-[70vh] object-contain"
                  />
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-400">
                    <Camera className="w-16 h-16" />
                  </div>
                )}
              </div>

              {/* Info bar */}
              <div className="p-3 flex items-center justify-between border-t">
                <div className="text-sm text-gray-600">
                  {attachments[lightboxIndex].description && (
                    <p className="font-medium">{attachments[lightboxIndex].description}</p>
                  )}
                  <p className="text-xs text-gray-400">
                    {new Date(attachments[lightboxIndex].created).toLocaleString('nb-NO')}
                    {' - '}{getDisplayName(attachments[lightboxIndex].operatorID)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (window.confirm(t('media.deleteConfirm', {}, 'Slett dette vedlegget?'))) {
                      handleDeleteAttachment(attachments[lightboxIndex!].id);
                    }
                  }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  title={t('common.buttons.delete', {}, 'Slett')}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Navigation arrows */}
            {attachments.length > 1 && (
              <>
                {lightboxIndex > 0 && (
                  <button
                    onClick={() => setLightboxIndex(lightboxIndex - 1)}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 p-2 text-white hover:text-gray-300"
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                )}
                {lightboxIndex < attachments.length - 1 && (
                  <button
                    onClick={() => setLightboxIndex(lightboxIndex + 1)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 p-2 text-white hover:text-gray-300"
                  >
                    <ChevronRight className="w-8 h-8" />
                  </button>
                )}
              </>
            )}

            {/* Counter */}
            <div className="text-center mt-3 text-white text-sm">
              {lightboxIndex + 1} / {attachments.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
